import moment from 'moment';

export const isImage = (mimeType: string | undefined | null) => {
  if (!mimeType) {
    return false;
  }
  return mimeType.startsWith('image');
};

export const isPdf = (mimeType: string | undefined | null) => {
  if (!mimeType) {
    return false;
  }
  return mimeType.startsWith('application/pdf');
};

export const calcDurationExcludingWeekend = (
  startDate: string,
  endDate: string,
) => {
  const mStartDate = new Date(startDate);
  const mEndDate = new Date(endDate);
  let diff = 0;
  while (mStartDate.getTime() < mEndDate.getTime()) {
    if (mStartDate.getUTCDay() !== 0 && mStartDate.getUTCDay() !== 6) {
      const end = new Date(mStartDate);
      end.setUTCHours(23, 59, 59, 999);
      if (end.getTime() < mEndDate.getTime()) {
        diff += end.getTime() - mStartDate.getTime();
      } else {
        diff += mEndDate.getTime() - mStartDate.getTime();
      }
    }
    mStartDate.setUTCDate(mStartDate.getUTCDate() + 1);
    mStartDate.setUTCHours(0, 0, 0, 0);
  }
  // console.log(
  //   'calcDurationExcludingWeekend',
  //   startDate,
  //   endDate,
  //   diff,
  //   diff / 1000 / 60 / 60
  // );
  return diff;
};

//
//  Calcola microsecondi effettivi tra due date, considerando solo
//  i giorni feriali (lunedì-venerdì) e un orario lavorativo specifico
//
//  @param {Date | string} from - La data/ora di inizio.
//  @param {Date | string} to - La data/ora di fine.
//  @param {number} workStartHour - L'orario di inizio lavoro.
//  @param {number} workEndHour - L'orario di fine lavoro.
//  @returns {number} Il numero totale di microsecondi lavorati
//
export const workingMs = (
  from: Date | string,
  to: Date | string,
  workStartHour: number,
  workEndHour: number,
) => {
  const startDate = new Date(from);
  const endDate = new Date(to);

  let totalWorkMs = 0;
  const current = new Date(startDate);

  while (current < endDate) {
    const dayOfWeek = current.getDay();

    const isWeekday = dayOfWeek !== 0 && dayOfWeek !== 6;

    if (isWeekday) {
      const shiftStart = new Date(current).setHours(workStartHour, 0, 0, 0);
      const shiftEnd = new Date(current).setHours(workEndHour, 0, 0, 0);

      const intervalStart = Math.max(
        current.getTime(), // Il nostro "cursore"
        shiftStart,
      );

      const intervalEnd = Math.min(endDate.getTime(), shiftEnd);

      if (intervalStart < intervalEnd) {
        totalWorkMs += intervalEnd - intervalStart;
      }
    }

    current.setDate(current.getDate() + 1);
    current.setHours(0, 0, 0, 0);
  }

  return totalWorkMs;
};

export const sort = (
  item1: any,
  item2: any,
  prop: string,
  caseSensitive = false,
  asc = true,
  prop2 = undefined, // check two different properties
) => {
  const item2Prop = prop2 || prop;
  let item1Descr = item1[prop] || '';
  let item2Descr = item2[item2Prop] || '';
  if (!caseSensitive) {
    item1Descr = item1Descr.toString().toLowerCase();
    item2Descr = item2Descr.toString().toLowerCase();
  }
  if (asc) {
    return item1Descr === item2Descr ? 0 : item1Descr < item2Descr ? -1 : 1;
  } else {
    return item1Descr === item2Descr ? 0 : item1Descr < item2Descr ? 1 : -1;
  }
};

export const sortNumber = (
  item1: any,
  item2: any,
  prop: string,
  asc = true,
  prop2 = undefined, // check two different properties
) => {
  const item2Prop = prop2 || prop;
  const item1Descr = +item1[prop];
  const item2Descr = +item2[item2Prop];
  if (asc) {
    return item1Descr - item2Descr;
  } else {
    return item2Descr - item1Descr;
  }
};
export const hasPermission = (
  permission: string,
  entity: string,
  permissions?: string[],
) => {
  const entityName = entity.replace('Entity', '').toLowerCase(); // entity[0].toLowerCase() + entity.slice(1).replace('Entity', '');
  // console.log('*-*-', permission, entityName, permissions);
  if (!permissions) {
    return false;
  }
  if (permissions?.indexOf(`${permission}_all`) > -1) {
    return true;
  } else {
    return permissions.indexOf(`${permission}_${entityName}`) > -1;
  }
};

export const mergeDateAndTime = (date: string | null, time: string | null) => {
  if (!date || !time) return null;
  const dateString = `${moment(date).format('YYYY-MM-DD')} ${moment(
    time,
  ).format('HH:mm:ss')}`;
  return moment(dateString, 'YYYY-MM-DD HH:mm:ss').toISOString();
};

export const getDescendantProp = (obj: any, path: string) => {
  const arr = path.split('.');
  while (arr.length && (obj = obj[arr.shift() as string]));
  return obj;
};
function padTo2Digits(num: number) {
  return num.toString().padStart(2, '0');
}
export function formatDate(date: Date) {
  return [
    date.getFullYear(),
    padTo2Digits(date.getMonth() + 1),
    padTo2Digits(date.getDate()),
  ].join('-');
}
export const isDate = (date: any) => {
  return !isNaN(Date.parse(date));
};

/**
 * Ripulisce un nome file (o foglio Excel) lasciando solo lettere, numeri, `.`, `_` e `-`.
 * Rimuove accenti, spazi e caratteri speciali (sostituiti con `-`), evita `..` e
 * punti/trattini iniziali e finali. Se il risultato è vuoto restituisce `fallback`.
 */
export function sanitizeFileName(
  input: string | null | undefined,
  fallback = 'file',
): string {
  const sanitized = String(input ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/\.{2,}/g, '.')
    .replace(/-{2,}/g, '-')
    .slice(0, 200)
    .replace(/^[-._]+|[-._]+$/g, '');
  return sanitized || fallback;
}

export const isEmail = (email: string | null | undefined): boolean => {
  if (!email || email === '' || email === null || email === undefined)
    return false;

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export type ExternalIdField = 'coswinExternalId' | 'easiExternalId';

// Converts the raw env value (e.g. "EasiExternalId") to a camelCase field name.
export const parseExternalIdField = (
  raw: string | undefined,
): ExternalIdField | undefined => {
  if (!raw) return undefined;
  const normalized = (raw.charAt(0).toLowerCase() +
    raw.slice(1)) as ExternalIdField;
  return normalized === 'coswinExternalId' || normalized === 'easiExternalId'
    ? normalized
    : undefined;
};

// Checks whether the active external ID field (from GLOBAL_SERVICE_EXTERNAL_ID_FIELD) is set.
// Pass the result of parseExternalIdField() as activeField.
export const hasExternalId = (
  entity:
    | { coswinExternalId?: string; easiExternalId?: string }
    | null
    | undefined,
  activeField: ExternalIdField | undefined,
): boolean => {
  if (!entity || !activeField) return false;
  return !!entity[activeField];
};
